// Force build counter: 2
import { createTheme, loadGoogleFont, StylesContext, ThemeWithColors } from '@trustmary/form-styles'
import { styled } from 'goober'
import 'promise-polyfill/src/polyfill'
import React, { useCallback, useEffect, useMemo, useReducer } from 'react'
import { render } from 'react-dom'
import { Page } from './components/page/Page'
import { ThemeContext } from './context/themeContext'
import { useGlobalStyles } from './globalStyles'
import { useSurveyStyles } from './hooks/useSurveyStyles'
import './misc/styled'

const StyledPage = styled('div')<{ $frame: boolean; fontFamily: string }>(({ $frame, fontFamily }) =>
  $frame
    ? {}
    : {
        fontFamily,
        padding: '20px 0 50px',
      }
)

const themeReducer = (state: ThemeWithColors, action: ThemeWithColors | undefined) => {
  return action ? action : createTheme()
}

const Root = () => {
  const locationQuery: { [key: string]: string } = useMemo(() => {
    return window.location.search
      .substr(1)
      .split('&')
      .reduce<{ [key: string]: string }>((mapped, str) => {
        const [key, value] = str.split('=')
        if (key) mapped[key] = value
        return mapped
      }, {})
  }, [])

  const frameId = locationQuery.frame

  const view = frameId ? 'frame' : 'page'

  const [theme, themeDispatch] = useReducer(themeReducer, createTheme({ view: view }))
  const { surveyStyles } = useSurveyStyles(theme)

  const onResize = useCallback(() => {
    try {
      parent.postMessage(
        {
          frameId,
          type: 'resize',
          attr: {
            height: document.body.offsetHeight,
          },
        },
        '*'
      )
    } catch (err) {
      console.log(err)
    }
  }, [frameId])

  useEffect(() => {
    if (view === 'frame') {
      onResize()
      window.addEventListener('resize', onResize)
      return () => {
        window.removeEventListener('resize', onResize)
      }
    }
  }, [view, onResize])

  useEffect(() => {
    if (theme.fontSource === 'google') {
      loadGoogleFont(theme.fontFamily)
    }
  }, [theme.fontSource, theme.fontFamily])

  // Setup global styles
  useGlobalStyles(theme)

  return (
    <StylesContext.Provider
      value={{
        styles: surveyStyles,
        values: theme,
      }}>
      <ThemeContext.Provider value={theme}>
        <StyledPage
          $frame={view === 'frame'}
          fontFamily={theme.fontFamily}
          className={`page page--${view === 'page' ? 'default' : view}`}>
          <Page themeDispatch={themeDispatch} onResize={onResize} />
        </StyledPage>
      </ThemeContext.Provider>
    </StylesContext.Provider>
  )
}

if (
  'Map' in window &&
  'forEach' in NodeList.prototype &&
  'startsWith' in String.prototype &&
  'endsWith' in String.prototype &&
  'includes' in String.prototype &&
  'includes' in Array.prototype &&
  'assign' in Object &&
  'entries' in Object &&
  'keys' in Object &&
  'lol' in Object
) {
  render(<Root />, document.getElementById('app'))
} else {
  void import('./polyfills').then(() => render(<Root />, document.getElementById('app')))
}
